import { SidebarMenuItemLinkMap } from 'features/sidebar/constants/sidebar-menu-item-link-map';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { SidebarMenuItemIconMap } from 'features/sidebar/constants/sidebar-menu-item-icon-map';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import * as React from 'react';
import { SidebarMenuItem } from 'features/sidebar/ts-types';
import { Link, useLocation } from 'react-router-dom';
import { Dispatch, Fragment, SetStateAction } from 'react';
import { trackEvent } from 'utils/amplitude.util';
import { AmplitudeEvents } from 'shared/enums/amplitude-events.enum';

type Props = {
  menuItem: SidebarMenuItem;
  setOpenedItems: Dispatch<SetStateAction<string[]>>;
  openedMenuItems: string[];
  disabled?: boolean;
};

const SidebarItem: React.FC<Props> = (props) => {
  const location = useLocation();

  const handleToggleExpand = (menuItem: SidebarMenuItem): void => {
    if (!!menuItem.children && menuItem.children.length > 0) {
      props.setOpenedItems((prev) =>
        prev.includes(menuItem.title)
          ? prev.filter((item) => item !== menuItem.title)
          : [...prev, menuItem.title]
      );
    }
  };

  const getItemLink = (item: SidebarMenuItem): string => {
    if (!item.link) return '#';
    const linkValue = SidebarMenuItemLinkMap[item.link];
    if (typeof linkValue === 'function') {
      try {
        return linkValue();
      } catch (e) {
        return '#';
      }
    }
    return linkValue || '#';
  };

  const isItemSelected = (item: SidebarMenuItem): boolean => {
    if (item.children?.length !== 0 || !item.link) return false;
    const linkString = getItemLink(item);
    return location.pathname === linkString;
  };

  const isItemOpened = (item: SidebarMenuItem): boolean =>
    props.openedMenuItems.includes(item.title);

  const hasChildren = !!props.menuItem.children?.length;
  const hasLink = !!props.menuItem.link && !hasChildren;

  const handleNavigationClick = (to: string) => {
    trackEvent(AmplitudeEvents.SIDEBAR_NAVIGATION_CLICKED, {
      from: location.pathname,
      to,
    });
  };

  return (
    <>
      {hasLink ? (
        <ListItemButton
          component={Link}
          to={getItemLink(props.menuItem)}
          onClick={() => handleNavigationClick(getItemLink(props.menuItem))}
          sx={{ height: '50px', textDecoration: 'none', color: 'inherit' }}
          selected={isItemSelected(props.menuItem)}
        >
          <ListItemIcon>
            {SidebarMenuItemIconMap[props.menuItem.title]}
          </ListItemIcon>
          <ListItemText primary={props.menuItem.title} />
        </ListItemButton>
      ) : (
        <ListItemButton
          sx={{ height: '50px' }}
          onClick={() => handleToggleExpand(props.menuItem)}
          selected={isItemSelected(props.menuItem)}
        >
          <ListItemIcon>
            {SidebarMenuItemIconMap[props.menuItem.title]}
          </ListItemIcon>
          <ListItemText primary={props.menuItem.title} />
          {hasChildren &&
            (isItemOpened(props.menuItem) ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      )}
      {!!props.menuItem.children?.length && (
        <Collapse
          in={props.openedMenuItems.includes(props.menuItem.title)}
          timeout="auto"
          unmountOnExit
        >
          <List component="div" disablePadding>
            {props.menuItem.children.map((childItem, indexChild) => (
              <Fragment key={indexChild}>
                {childItem.link && !childItem.disabled ? (
                  <ListItemButton
                    component={Link}
                    to={getItemLink(childItem)}
                    onClick={() =>
                      handleNavigationClick(getItemLink(childItem))
                    }
                    sx={{ pl: 9, textDecoration: 'none', color: 'inherit' }}
                    selected={isItemSelected(childItem)}
                  >
                    <ListItemText primary={childItem.title} />
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    disabled={!!childItem.disabled}
                    sx={{ pl: 9 }}
                    selected={isItemSelected(childItem)}
                  >
                    <ListItemText primary={childItem.title} />
                  </ListItemButton>
                )}
              </Fragment>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};
export default SidebarItem;
